import { toast } from "@/components/ui/sonner";

export interface CartAddSource {
  x: number;
  y: number;
  icon: SVGSVGElement | null;
}

export const captureCartAddSource = (button: HTMLButtonElement): CartAddSource => {
  const icon = button.querySelector("svg");
  const rect = (icon || button).getBoundingClientRect();

  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
    icon: icon?.cloneNode(true) as SVGSVGElement | null,
  };
};

export const playCartAddFeedback = (source: CartAddSource, productName: string) => {
  const showSuccess = () => {
    toast.success("Successfully added to cart", {
      description: productName,
      duration: 2600,
    });
  };

  const target = document.querySelector<HTMLButtonElement>("[data-cart-target]");
  const targetIcon = target?.querySelector("svg");

  if (!source.icon || !target || !targetIcon || !source.x || !source.y ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      typeof document.body.animate !== "function") {
    showSuccess();
    return Promise.resolve();
  }

  const targetRect = targetIcon.getBoundingClientRect();
  const targetX = targetRect.left + targetRect.width / 2;
  const targetY = targetRect.top + targetRect.height / 2;
  const offsetX = targetX - source.x;
  const offsetY = targetY - source.y;
  const arc = Math.min(90, Math.max(32, Math.abs(offsetY) * 0.18));

  const flyer = document.createElement("div");
  Object.assign(flyer.style, {
    position: "fixed",
    left: `${source.x - 21}px`,
    top: `${source.y - 21}px`,
    width: "42px",
    height: "42px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #ea580c, #ca8a04)",
    color: "white",
    boxShadow: "0 8px 24px rgba(234, 88, 12, 0.35)",
    pointerEvents: "none",
    zIndex: "100",
  });
  source.icon.style.width = "20px";
  source.icon.style.height = "20px";
  flyer.append(source.icon);
  document.body.append(flyer);

  const flight = flyer.animate([
    { transform: "translate(0, 0) scale(0.7)", opacity: 0.6 },
    { transform: `translate(${offsetX * 0.45}px, ${offsetY * 0.45 - arc}px) scale(1.15)`, opacity: 1, offset: 0.48 },
    { transform: `translate(${offsetX}px, ${offsetY}px) scale(0.35)`, opacity: 0.8 },
  ], { duration: 680, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "forwards" });

  return new Promise<void>((resolve) => {
    flight.onfinish = () => {
      flyer.remove();
      if (target.isConnected) {
        targetIcon.animate([
          { transform: "scale(1)" },
          { transform: "scale(1.35)", offset: 0.45 },
          { transform: "scale(1)" },
        ], { duration: 380, easing: "ease-out" });
      }
      showSuccess();
      resolve();
    };
    flight.oncancel = () => {
      flyer.remove();
      showSuccess();
      resolve();
    };
  });
};
